import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { legacyApi, DeliveryNoteDetail } from '../../../services/legacyApi';
import { ArrowLeft, CreditCard, Download, Link2, Loader2, MessageCircle, Package, Pencil, Save, Send, Trash2, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { getDeliveryStatusMeta } from '../utils/statuses';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { useAuth } from '../../../context/AuthContext';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { openWhatsAppMessage } from '../utils/whatsapp';

export const LegacyDeliveryDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { confirm } = useConfirm();
    const { user, hasPermission } = useAuth();
    const [delivery, setDelivery] = useState<DeliveryNoteDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [warningBanner, setWarningBanner] = useState<string | null>(null);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);

    const [lineOptions, setLineOptions] = useState<Array<{
        key: string;
        label: string;
        itemCode?: string;
        so_detail_id?: number;
        estimate_part_id?: number;
        maxShippable: number;
    }>>([]);
    const [editedItems, setEditedItems] = useState<Array<{
        so_detail_id?: number;
        estimate_part_id?: number;
        quantity_shipped: number;
    }>>([]);

    // Editable fields
    const [editedDate, setEditedDate] = useState('');
    const [editedRemarks, setEditedRemarks] = useState('');
    const [editedRef, setEditedRef] = useState('');
    const [editedLocation, setEditedLocation] = useState('');
    const [editedPoRef, setEditedPoRef] = useState('');
    const [editedCreatedBy, setEditedCreatedBy] = useState('');

    const isAdmin = (user?.role || '').toLowerCase() === 'admin';
    const canUndoDispatch = isAdmin && hasPermission('sales.delivery.undispatch');

    const makeLineKey = (line: { so_detail_id?: number; estimate_part_id?: number }) =>
        line.so_detail_id !== undefined && line.so_detail_id !== null ? `so:${line.so_detail_id}` : `ep:${line.estimate_part_id ?? '0'}`;

    const hydrateLineOptions = async (dn: DeliveryNoteDetail) => {
        try {
            const [order, deliveries] = await Promise.all([
                legacyApi.sales.getOrder(dn.so_id),
                legacyApi.sales.getDeliveriesForOrder(dn.so_id),
            ]);

            const allocatedByDetail = new Map<number, number>();
            const allocatedByPart = new Map<number, number>();
            const activeDeliveries = deliveries.filter((item) => Number(item.status_id || 1) !== 3 && Number(item.dn_id) !== Number(dn.dn_id));
            if (activeDeliveries.length > 0) {
                const details = await Promise.all(
                    activeDeliveries.map(async (item) => {
                        try {
                            return await legacyApi.sales.getDetail(Number(item.dn_id));
                        } catch {
                            return null;
                        }
                    })
                );
                for (const entry of details) {
                    if (!entry) continue;
                    for (const item of entry.items || []) {
                        const qty = Number(item.quantity_shipped || 0);
                        if (qty <= 0) continue;
                        if (item.so_detail_id !== undefined && item.so_detail_id !== null) {
                            const key = Number(item.so_detail_id);
                            allocatedByDetail.set(key, (allocatedByDetail.get(key) || 0) + qty);
                        }
                        if (item.estimate_part_id !== undefined && item.estimate_part_id !== null) {
                            const key = Number(item.estimate_part_id);
                            allocatedByPart.set(key, (allocatedByPart.get(key) || 0) + qty);
                        }
                    }
                }
            }

            const options = (order.details || []).map((line) => {
                const soDetailId = line.so_detail_id !== undefined && line.so_detail_id !== null ? Number(line.so_detail_id) : undefined;
                const estimatePartId = line.estimate_part_id !== undefined && line.estimate_part_id !== null ? Number(line.estimate_part_id) : undefined;
                const ordered = Number(line.quantity || 0);
                const allocated = soDetailId !== undefined
                    ? Number(allocatedByDetail.get(soDetailId) || 0)
                    : Number(allocatedByPart.get(Number(estimatePartId || 0)) || 0);
                const maxShippable = Math.max(0, ordered - allocated);
                const itemCode = String(line.item_code || line.formatted_part_id || (line.item_id ? `ITEM-${line.item_id}` : '')).trim();
                return {
                    key: makeLineKey({ so_detail_id: soDetailId, estimate_part_id: estimatePartId }),
                    label: line.part_name || `Part ${estimatePartId ?? soDetailId ?? ''}`,
                    itemCode: itemCode || undefined,
                    so_detail_id: soDetailId,
                    estimate_part_id: estimatePartId,
                    maxShippable,
                };
            });
            setLineOptions(options);
        } catch (err) {
            console.error('Failed to hydrate delivery line options', err);
            setLineOptions([]);
        }
    };

    const loadDelivery = async (autoEdit = false) => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await legacyApi.sales.getDetail(parseInt(id));
            setDelivery(data);
            setEditedDate(data.dn_date ? new Date(data.dn_date).toISOString().split('T')[0] : '');
            setEditedRemarks(data.remarks || '');
            setEditedRef(data.dn_ref || '');
            setEditedLocation(data.delivery_location || '');
            setEditedPoRef(data.po_ref || '');
            setEditedCreatedBy(data.created_by || '');
            setEditedItems((data.items || []).map((item) => ({
                so_detail_id: item.so_detail_id,
                estimate_part_id: item.estimate_part_id,
                quantity_shipped: Number(item.quantity_shipped || 0),
            })));
            await hydrateLineOptions(data);
            const isDraft = Number(data.status_id || 1) === 1;
            if (autoEdit && isDraft) {
                setEditMode(true);
            } else {
                setEditMode(false);
                if (autoEdit && !isDraft) {
                    toast.info('Only Draft delivery notes can be edited. Use Undo Dispatch first.');
                    navigate(`/legacy/delivery/${data.dn_id}`, { replace: true });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setWarningBanner(null);
        loadDelivery(searchParams.get('edit') === '1');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, searchParams]);

    const handleDownload = async () => {
        if (!delivery) return;
        try {
            await legacyApi.sales.downloadDeliveryNote(delivery.dn_id);
        } catch (err) {
            toast.error('Failed to download Delivery Note');
            console.error(err);
        }
    };

    const handleSave = async () => {
        if (!delivery) return;
        if (Number(delivery.status_id || 1) !== 1) {
            toast.error('Only Draft delivery notes can be edited.');
            setEditMode(false);
            return;
        }
        try {
            setSaving(true);
            const itemsPayload = editedItems
                .map((item) => ({
                    so_detail_id: item.so_detail_id,
                    estimate_part_id: item.estimate_part_id,
                    quantity_shipped: Number(item.quantity_shipped || 0),
                }))
                .filter((item) => item.quantity_shipped > 0);
            if (itemsPayload.length === 0) {
                toast.error('Add at least one line with shipped quantity greater than zero.');
                return;
            }
            const updated = await legacyApi.sales.updateDelivery(delivery.dn_id, {
                dn_date: editedDate,
                remarks: editedRemarks,
                dn_ref: editedRef,
                delivery_location: editedLocation,
                po_ref: editedPoRef,
                created_by: editedCreatedBy,
                items: itemsPayload,
            });
            setDelivery({ ...delivery, ...updated, items: itemsPayload });
            await loadDelivery(false);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to update delivery note');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!delivery) return;
        const confirmed = await confirm(`Delete Delivery Note ${delivery.dn_number}?`, {
            description: 'This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive'
        });
        if (!confirmed) return;

        try {
            await legacyApi.sales.deleteDelivery(delivery.dn_id);
            navigate(`/legacy/orders/${delivery.so_id}`);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete delivery note');
        }
    };

    const handleDispatch = async () => {
        if (!delivery) return;
        try {
            const stockPrecheck = await legacyApi.sales.getDeliveryDispatchPrecheck(delivery.dn_id);
            if (!Boolean(stockPrecheck?.can_dispatch)) {
                toast.error(stockPrecheck?.dispatch_reason || 'Dispatch blocked by stock precheck');
                return;
            }
        } catch (e: any) {
            toast.error(e?.message || 'Failed to run dispatch stock precheck');
            return;
        }
        try {
            const precheck = await legacyApi.workflow.precheck({
                entity: 'DN',
                entity_id: delivery.dn_id,
                action: 'dispatch',
            });
            if (String(precheck.overall_status || '').toLowerCase() === 'fail') {
                toast.error(precheck.checks?.[0]?.message || 'Dispatch precheck failed');
                return;
            }
        } catch (e: any) {
            toast.error(e?.message || 'Failed to run dispatch precheck');
            return;
        }
        const confirmed = await confirm(`Dispatch Delivery Note ${delivery.dn_number}?`, {
            description: 'This will deduct inventory and post COGS. Action cannot be undone.',
            confirmText: 'Dispatch',
            variant: 'default'
        });
        if (!confirmed) return;

        try {
            setSaving(true);
            const res = await legacyApi.sales.dispatchDelivery(delivery.dn_id);
            const refreshed = await legacyApi.sales.getDetail(delivery.dn_id);
            setDelivery(refreshed);
            await hydrateLineOptions(refreshed);
            toast.success('Delivery dispatched successfully');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to dispatch delivery');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelDN = async () => {
        if (!delivery) return;
        try {
            const precheck = await legacyApi.workflow.precheck({
                entity: 'DN',
                entity_id: delivery.dn_id,
                action: 'cancel',
            });
            if (String(precheck.overall_status || '').toLowerCase() === 'fail') {
                toast.error(precheck.checks?.[0]?.message || 'Cancel precheck failed');
                return;
            }
        } catch (e: any) {
            toast.error(e?.message || 'Failed to run cancel precheck');
            return;
        }
        const confirmed = await confirm(`Cancel Delivery Note ${delivery.dn_number}?`, {
            description: 'This will mark the delivery as cancelled.',
            confirmText: 'Cancel Delivery',
            variant: 'destructive'
        });
        if (!confirmed) return;

        try {
            setSaving(true);
            await legacyApi.sales.cancelDelivery(delivery.dn_id);
            setDelivery({ ...delivery, status_id: 3 });
            toast.success('Delivery cancelled successfully');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to cancel delivery');
        } finally {
            setSaving(false);
        }
    };

    const resolveDeliveryRecipientEmail = async () => {
        if (!delivery?.so_id) return '';
        const [order, customers] = await Promise.all([
            legacyApi.sales.getOrder(delivery.so_id),
            legacyApi.customers.list(),
        ]);
        return customers.find((customer) => Number(customer.customer_id) === Number(order.customer_id))?.email_address || '';
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!delivery) return;
        const result = await legacyApi.sales.emailDeliveryPdf(delivery.dn_id, payload);
        toast.success(result.message || 'Delivery note PDF emailed successfully');
    };

    const handleShareLink = async () => {
        if (!delivery) return;
        try {
            const result = await legacyApi.sales.createDeliveryShareLink(delivery.dn_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Delivery note share link created and copied' : 'Delivery note share link copied');
            } catch {
                window.prompt('Copy delivery note share link', result.share_url);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create delivery note share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!delivery) return;
        try {
            const result = await legacyApi.sales.createDeliveryShareLink(delivery.dn_id, { channel: 'WHATSAPP' });
            const message = `Please find delivery note ${delivery.dn_number}: ${result.share_url}`;
            const [order, customers] = await Promise.all([
                legacyApi.sales.getOrder(delivery.so_id),
                legacyApi.customers.list(),
            ]);
            const customer = customers.find((entry) => Number(entry.customer_id) === Number(order.customer_id));
            const resolved = openWhatsAppMessage(message, customer);
            if (!resolved.normalizedNumber) {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open WhatsApp share');
        }
    };

    const handleUndoDispatch = async () => {
        if (!delivery) return;
        const confirmed = await confirm(`Undo dispatch for ${delivery.dn_number}?`, {
            description: 'This will reverse stock/COGS and reopen the delivery note in Draft for editing.',
            confirmText: 'Undo Dispatch',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            setSaving(true);
            const response = await legacyApi.sales.undoDispatchDelivery(delivery.dn_id);
            const warnings = Array.isArray(response?.undispatch_warning) ? response.undispatch_warning.filter(Boolean) : [];
            setWarningBanner(warnings.length > 0 ? warnings.join(' ') : null);
            await loadDelivery(false);
            toast.success('Delivery note moved back to Draft.');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to undo dispatch');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateSpotInvoice = async () => {
        if (!delivery) return;
        if (Number(delivery.status_id || 1) !== 2) {
            toast.error('Only dispatched delivery notes can be invoiced.');
            return;
        }
        try {
            setCreatingInvoice(true);
            const preview = await legacyApi.sales.previewUnbilledDeliveryInvoice({
                so_id: Number(delivery.so_id),
                mode: 'spot',
                dn_id: Number(delivery.dn_id),
            });
            if (!preview.can_create) {
                toast.error(preview.reason || 'No unbilled quantity available for this delivery note.');
                return;
            }
            const created = await legacyApi.sales.createInvoiceFromUnbilledDeliveries({
                so_id: Number(delivery.so_id),
                mode: 'spot',
                dn_id: Number(delivery.dn_id),
                invoice_notes: `Spot invoice from ${delivery.dn_number}`,
            });
            toast.success('Spot invoice created for delivered unbilled quantity.');
            navigate(`/legacy/invoices/${created.invoice_id}`);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to create spot invoice');
        } finally {
            setCreatingInvoice(false);
        }
    };

    const updateEditedItem = (
        index: number,
        patch: Partial<{ so_detail_id?: number; estimate_part_id?: number; quantity_shipped: number }>
    ) => {
        setEditedItems((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
    };

    const addEditedItem = () => {
        const firstOption = lineOptions[0];
        setEditedItems((prev) => [
            ...prev,
            {
                so_detail_id: firstOption?.so_detail_id,
                estimate_part_id: firstOption?.estimate_part_id,
                quantity_shipped: firstOption ? Number(firstOption.maxShippable || 0) : 0,
            },
        ]);
    };

    const removeEditedItem = (index: number) => {
        setEditedItems((prev) => prev.filter((_, idx) => idx !== index));
    };

    const getStatusBadge = (statusId: number) => {
        const meta = getDeliveryStatusMeta(statusId);
        return <span className={`px-2 py-1 rounded text-xs font-medium ${meta.badgeClass}`}>{meta.label}</span>;
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (!delivery) return <div className="p-8 text-center text-red-500">Delivery Note not found</div>;

    const isLocked = delivery.status_id !== 1; // Locked if not Draft
    const lineOptionByKey = new Map(lineOptions.map((item) => [item.key, item]));
    const isDraftDelivery = Number(delivery.status_id || 1) === 1;
    const itemsSectionTitle = isDraftDelivery ? 'Planned Items' : 'Items Shipped';
    const quantityHeaderLabel = isDraftDelivery ? 'Planned Qty' : 'Quantity Shipped';
    const canCreateSpotInvoiceFromDn = Number(delivery.status_id || 1) === 2;
    const canCreateTaxInvoiceFromDn = Boolean(delivery.can_create_tax_invoice ?? canCreateSpotInvoiceFromDn);
    const createTaxInvoiceReason = String(
        delivery.create_tax_invoice_reason || (!canCreateTaxInvoiceFromDn ? 'Only dispatched delivery notes can be invoiced.' : '')
    );
    const canDispatchFromDn = Boolean(delivery.can_dispatch ?? (Number(delivery.status_id || 1) === 1));
    const dispatchReason = String(
        delivery.dispatch_reason || (!canDispatchFromDn ? 'Dispatch precheck failed. Review item stock mapping.' : 'Ready to dispatch.')
    );
    const dispatchBlockedLines = Array.isArray(delivery.dispatch_checks)
        ? delivery.dispatch_checks.filter((line) => !Boolean(line.stock_ok))
        : [];

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
            <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0 flex items-center justify-between shadow-sm">
                <SendPdfModal
                    open={showSendPdfModal}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Delivery Note"
                    documentNumber={delivery.dn_number}
                    attachmentName={`${String(delivery.dn_number || `HC_DN_${delivery.dn_id}`).replace(/\//g, '_')}.pdf`}
                    defaultSubject={`Delivery Note ${delivery.dn_number}`}
                    defaultBody={`Dear Customer,\n\nPlease find attached delivery note ${delivery.dn_number}.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPdf}
                    resolveDefaultTo={resolveDeliveryRecipientEmail}
                />
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate(`/legacy/orders/${delivery.so_id}`)}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-bold tracking-tight text-foreground">
                                {editMode ? 'Edit Delivery Note' : delivery.dn_number}
                            </h1>
                            {getStatusBadge(delivery.status_id || 1)}
                        </div>
                        <p className="text-muted-foreground">
                            {editMode ? `Updating dispatch details for ${delivery.dn_number}` : `Date: ${new Date(delivery.dn_date).toLocaleDateString()}`}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {!editMode ? (
                        <>
                            {!isLocked && (
                                <Button variant="outline" onClick={() => setEditMode(true)}>
                                    <Pencil className="h-4 w-4 mr-2" /> Edit
                                </Button>
                            )}
                            {!isLocked && (
                                <>
                                <Button
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={handleDispatch}
                                    disabled={saving || !canDispatchFromDn}
                                    title={!canDispatchFromDn ? dispatchReason : 'Dispatch'}
                                >
                                    <Package className="h-4 w-4 mr-2" /> Dispatch
                                </Button>
                                    <Button variant="destructive" onClick={handleCancelDN}>
                                        <X className="h-4 w-4 mr-2" /> Cancel
                                    </Button>
                                </>
                            )}
                            {canUndoDispatch && delivery.status_id === 2 && (
                                <Button variant="outline" onClick={handleUndoDispatch} disabled={saving}>
                                    <X className="h-4 w-4 mr-2" /> Undo Dispatch
                                </Button>
                            )}
                            {hasPermission('finance.invoice.create') && (
                                <Button
                                    variant="outline"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    onClick={handleCreateSpotInvoice}
                                    disabled={creatingInvoice || !canCreateSpotInvoiceFromDn}
                                    title={!canCreateSpotInvoiceFromDn ? 'Only dispatched delivery notes can be invoiced.' : 'Create a tax invoice only for this DN delivered and unbilled quantity'}
                                >
                                    {creatingInvoice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                                    Create Spot Invoice
                                </Button>
                            )}
                            <Button variant="outline" onClick={handleDownload}>
                                <Download className="h-4 w-4 mr-2" /> Download PDF
                            </Button>
                            {hasPermission('sales.delivery.email') && (
                                <Button variant="outline" onClick={() => setShowSendPdfModal(true)}>
                                    <Send className="h-4 w-4 mr-2" /> Send
                                </Button>
                            )}
                            {hasPermission('sales.delivery.share') && (
                                <>
                                    <Button variant="outline" onClick={handleShareLink}>
                                        <Link2 className="h-4 w-4 mr-2" /> Share Link
                                    </Button>
                                    <Button variant="outline" onClick={handleShareWhatsApp}>
                                        <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                                    </Button>
                                </>
                            )}
                            {!isLocked && (
                                <Button variant="destructive" onClick={handleDelete}>
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button variant="default" onClick={handleSave} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save Changes
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setEditedItems((delivery.items || []).map((item) => ({
                                        so_detail_id: item.so_detail_id,
                                        estimate_part_id: item.estimate_part_id,
                                        quantity_shipped: Number(item.quantity_shipped || 0),
                                    })));
                                    setEditMode(false);
                                }}
                                disabled={saving}
                            >
                                <X className="h-4 w-4 mr-2" /> Cancel
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-6">
                {warningBanner && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {warningBanner}
                    </div>
                )}
                {!canDispatchFromDn && isDraftDelivery && (
                    <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
                        {dispatchReason}
                        {dispatchBlockedLines.length > 0 && (
                            <div className="mt-1 text-xs text-red-700">
                                First blocked line: {dispatchBlockedLines[0]?.item_code || dispatchBlockedLines[0]?.part_name || 'Unknown item'}.
                            </div>
                        )}
                    </div>
                )}
                {canCreateSpotInvoiceFromDn && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        Existing posted invoices will not be changed. This invoice will include only delivered and unbilled quantities.
                        {!canCreateTaxInvoiceFromDn && createTaxInvoiceReason && (
                            <div className="mt-1 text-xs text-blue-800">
                                Legacy create is blocked: {createTaxInvoiceReason}. Use Create Spot Invoice for remaining unbilled delivery quantity.
                            </div>
                        )}
                    </div>
                )}
                <div className="space-y-6">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Delivery Details</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label>Dispatch Date</Label>
                            {editMode ? (
                                <Input type="date" value={editedDate} onChange={e => setEditedDate(e.target.value)} />
                            ) : (
                                <div className="p-2 border rounded bg-slate-50 text-sm font-medium">{new Date(delivery.dn_date).toLocaleDateString()}</div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>PO Ref#</Label>
                            {editMode ? (
                                <Input value={editedPoRef} onChange={e => setEditedPoRef(e.target.value)} />
                            ) : (
                                <div className="p-2 border rounded bg-slate-50 text-sm font-medium">{delivery.po_ref || '-'}</div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>DN Ref#</Label>
                            {editMode ? (
                                <Input value={editedRef} onChange={e => setEditedRef(e.target.value)} />
                            ) : (
                                <div className="p-2 border rounded bg-slate-50 text-sm font-medium">{delivery.dn_ref || '-'}</div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Created By</Label>
                            {editMode ? (
                                <Input value={editedCreatedBy} onChange={e => setEditedCreatedBy(e.target.value)} />
                            ) : (
                                <div className="p-2 border rounded bg-slate-50 text-sm font-medium">{delivery.created_by || '-'}</div>
                            )}
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Remarks</Label>
                            {editMode ? (
                                <Input
                                    placeholder="Driver name, vehicle no, etc."
                                    value={editedRemarks}
                                    onChange={e => setEditedRemarks(e.target.value)}
                                />
                            ) : (
                                <div className="p-2 border rounded bg-slate-50 text-sm min-h-[40px] font-medium">{delivery.remarks || '-'}</div>
                            )}
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Delivery Location</Label>
                            {editMode ? (
                                <textarea
                                    className="min-h-[90px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    placeholder="Delivery address / location"
                                    value={editedLocation}
                                    onChange={e => setEditedLocation(e.target.value)}
                                />
                            ) : (
                                <div className="p-3 border rounded bg-slate-50 text-sm min-h-[90px] whitespace-pre-wrap font-medium">{delivery.delivery_location || '-'}</div>
                            )}
                        </div>
                            </CardContent>
                        </Card>
                    </div>
                    <WorkflowRightSidebar mode="overlay" title="Delivery Workflow & Status">
                        <WorkflowRightPanel
                            title="Workflow & Status"
                            description="Current status, next action, and prechecks"
                        >
                            <FlowCoachPanel entity="DN" entityId={delivery.dn_id} />
                        </WorkflowRightPanel>
                    </WorkflowRightSidebar>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{itemsSectionTitle}</CardTitle>
                        {editMode && (
                            <Button type="button" variant="outline" size="sm" onClick={addEditedItem}>
                                Add Line
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-medium text-slate-600">Code</th>
                                        <th className="px-4 py-3 text-left font-medium text-slate-600">Part</th>
                                        <th className="px-4 py-3 text-right font-medium text-slate-600">{quantityHeaderLabel}</th>
                                        {editMode && <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {editMode ? (
                                        editedItems.map((item, idx) => {
                                            const selectedKey = makeLineKey(item);
                                            const selectedOption = lineOptionByKey.get(selectedKey);
                                            return (
                                                <tr key={`edit-line-${idx}`} className="hover:bg-slate-50/50">
                                                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                                                        {selectedOption?.itemCode || '-'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                                                            value={selectedKey}
                                                            onChange={(event) => {
                                                                const option = lineOptionByKey.get(event.target.value);
                                                                if (!option) return;
                                                                updateEditedItem(idx, {
                                                                    so_detail_id: option.so_detail_id,
                                                                    estimate_part_id: option.estimate_part_id,
                                                                    quantity_shipped: Math.min(
                                                                        Number(item.quantity_shipped || 0),
                                                                        Number(option.maxShippable || 0),
                                                                    ),
                                                                });
                                                            }}
                                                        >
                                                            {lineOptions.map((option) => (
                                                                <option key={option.key} value={option.key}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {selectedOption && (
                                                            <div className="pt-1 text-xs text-slate-500">
                                                                Max shippable: {selectedOption.maxShippable}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="0.001"
                                                            value={item.quantity_shipped}
                                                            onChange={(event) => {
                                                                const raw = Number(event.target.value || 0);
                                                                const max = Number(selectedOption?.maxShippable || 0);
                                                                const qty = Number.isFinite(raw) ? Math.max(0, Math.min(raw, max)) : 0;
                                                                updateEditedItem(idx, { quantity_shipped: qty });
                                                            }}
                                                            className="ml-auto w-28 text-right"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <Button type="button" variant="ghost" size="sm" onClick={() => removeEditedItem(idx)}>
                                                            Remove
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        delivery.items.map((item, idx) => {
                                            const key = makeLineKey(item);
                                            const option = lineOptionByKey.get(key);
                                            const label = item.part_name || option?.label || `PART-${item.estimate_part_id ?? item.so_detail_id ?? '-'}`;
                                            const code = item.item_code || option?.itemCode || '-';
                                            return (
                                                <tr key={idx} className="hover:bg-slate-50/50">
                                                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">{code}</td>
                                                    <td className="px-4 py-3">{label}</td>
                                                    <td className="px-4 py-3 text-right font-medium">{item.quantity_shipped}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
